/**
 * ICS (iCalendar) export utility for meeting events
 * Generates RFC 5545 compliant calendar files for cross-platform compatibility
 */

import type { Meeting } from '@penticton-dev-events/shared';

export type ICSEvent = {
  uid: string;
  title: string;
  description: string;
  startDateTime: Date;
  endDateTime: Date;
  location?: string;
  url?: string;
  organizer?: string;
  created: Date;
  lastModified: Date;
};

/**
 * Escapes special characters in ICS text fields according to RFC 5545
 */
const escapeICSText = (text: string): string => {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '');
};

/**
 * Formats a date for ICS format (YYYYMMDDTHHMMSSZ)
 */
const formatICSDateTime = (date: Date): string => {
  return date
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}Z$/, 'Z');
};

/**
 * Wraps long lines to 75 characters as required by RFC 5545
 */
const wrapICSLine = (line: string): string => {
  if (line.length <= 75) return line;

  const wrapped: string[] = [];
  let currentLine = line;

  while (currentLine.length > 75) {
    wrapped.push(currentLine.substring(0, 75));
    currentLine = ' ' + currentLine.substring(75); // Leading space for continuation
  }

  if (currentLine.length > 0) {
    wrapped.push(currentLine);
  }

  return wrapped.join('\n');
};

/**
 * Generates ICS content for a meeting event
 */
export const generateICSContent = (meeting: Meeting, groupName?: string): string => {
  const now = new Date();
  const startDate = new Date(meeting.startsAt);
  const endDate = new Date(meeting.endsAt);

  // Build location string
  let locationString = '';
  if (meeting.location) {
    locationString = meeting.location.label;
    if (meeting.location.address) {
      locationString += `, ${meeting.location.address}`;
    }
  }
  if (meeting.onlineUrl && locationString) {
    locationString += ` / Online: ${meeting.onlineUrl}`;
  } else if (meeting.onlineUrl) {
    locationString = `Online: ${meeting.onlineUrl}`;
  }

  // Build description with enhanced details
  let description = meeting.description;

  if (groupName) {
    description += `\\n\\nOrganized by: ${groupName}`;
  }

  if (meeting.capacity) {
    description += `\\n\\nCapacity: ${meeting.capacity} attendees`;
  }

  if (meeting.agenda && meeting.agenda.length > 0) {
    description += '\\n\\nAgenda:';
    meeting.agenda
      .sort((a, b) => a.order - b.order)
      .forEach((item, index) => {
        description += `\\n${index + 1}. ${item.title}`;
        if (item.duration) {
          description += ` (${item.duration} min)`;
        }
        if (item.presenter) {
          description += ` - ${item.presenter}`;
        }
        if (item.description) {
          description += `\\n   ${item.description}`;
        }
      });
  }

  if (meeting.coHosts && meeting.coHosts.length > 0) {
    description += '\\n\\nCo-hosts: ';
    description += meeting.coHosts
      .map(coHost => `${coHost.displayName}${coHost.role ? ` (${coHost.role})` : ''}`)
      .join(', ');
  }

  if (meeting.tags && meeting.tags.length > 0) {
    description += `\\n\\nTags: ${meeting.tags.join(', ')}`;
  }

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Penticton.dev Events//Event Calendar//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:meeting-${meeting.id}@events.penticton.dev`,
    `DTSTAMP:${formatICSDateTime(now)}`,
    `DTSTART:${formatICSDateTime(startDate)}`,
    `DTEND:${formatICSDateTime(endDate)}`,
    `CREATED:${formatICSDateTime(new Date(meeting.createdAt))}`,
    `LAST-MODIFIED:${formatICSDateTime(now)}`,
    `SUMMARY:${escapeICSText(meeting.title)}`,
    `DESCRIPTION:${escapeICSText(description)}`,
  ];

  if (locationString) {
    lines.push(`LOCATION:${escapeICSText(locationString)}`);
  }

  if (meeting.onlineUrl) {
    lines.push(`URL:${meeting.onlineUrl}`);
  }

  // Add status
  const status = meeting.status === 'scheduled' ? 'CONFIRMED' : 'CANCELLED';
  lines.push(`STATUS:${status}`);

  // Add transparency (show as busy)
  lines.push('TRANSP:OPAQUE');

  lines.push('END:VEVENT');
  lines.push('END:VCALENDAR');

  // Wrap long lines according to RFC 5545
  return lines.map(wrapICSLine).join('\n');
};

/**
 * Downloads an ICS file for a meeting
 */
export const downloadICSFile = (meeting: Meeting, groupName?: string): void => {
  const icsContent = generateICSContent(meeting, groupName);
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });

  // Create download link
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  // Generate filename
  const safeTitle = meeting.title
    .replace(/[^a-zA-Z0-9-_]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, ''); // Remove leading/trailing dashes
  const dateStr = new Date(meeting.startsAt).toISOString().split('T')[0]; // YYYY-MM-DD
  const filename = `${safeTitle}-${dateStr}.ics`;

  link.href = url;
  link.download = filename;
  link.style.display = 'none';

  // Trigger download
  document.body.appendChild(link);
  link.click();

  // Cleanup
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Generates a shareable calendar URL (for services that support webcal://)
 */
export const generateCalendarURL = (meeting: Meeting, groupName?: string): string => {
  const icsContent = generateICSContent(meeting, groupName);
  const encodedContent = encodeURIComponent(icsContent);

  // Return data URL that can be used with calendar applications
  return `data:text/calendar;charset=utf-8,${encodedContent}`;
};

/**
 * Generates Google Calendar add event URL
 */
export const generateGoogleCalendarURL = (meeting: Meeting): string => {
  const baseURL = 'https://calendar.google.com/calendar/render';
  const params = new URLSearchParams();

  params.set('action', 'TEMPLATE');
  params.set('text', meeting.title);
  params.set(
    'dates',
    `${formatICSDateTime(new Date(meeting.startsAt))}/${formatICSDateTime(new Date(meeting.endsAt))}`,
  );
  params.set('details', meeting.description);

  if (meeting.location) {
    let location = meeting.location.label;
    if (meeting.location.address) {
      location += `, ${meeting.location.address}`;
    }
    params.set('location', location);
  }

  if (meeting.onlineUrl) {
    params.set('add', meeting.onlineUrl);
  }

  return `${baseURL}?${params.toString()}`;
};

/**
 * Generates Outlook add event URL
 */
export const generateOutlookURL = (meeting: Meeting): string => {
  const baseURL = 'https://outlook.live.com/calendar/0/deeplink/compose';
  const params = new URLSearchParams();

  params.set('subject', meeting.title);
  params.set('startdt', new Date(meeting.startsAt).toISOString());
  params.set('enddt', new Date(meeting.endsAt).toISOString());
  params.set('body', meeting.description);

  if (meeting.location) {
    let location = meeting.location.label;
    if (meeting.location.address) {
      location += `, ${meeting.location.address}`;
    }
    params.set('location', location);
  }

  return `${baseURL}?${params.toString()}`;
};
